import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";

/**
 * Workflow API.
 *
 * Talks to the Express middleware at `/api/workflows`. The middleware
 * resolves the user from the session cookie and forwards to the Workflow
 * service, which internally handles saving/unlocking contacts. Only "email"
 * workflows exist - the waterfall tries email providers in rank order,
 * checkpointing an unconfirmed find through Email-Verification, until one is
 * verified or the list runs out.
 */

/**
 * Retry payloads. Identity is the same (personId, companyId) as a create -
 * these endpoints mint their own workflow rather than re-running an existing
 * one, because re-POSTing create hits its idempotency check and returns the
 * old finished workflow without dispatching anything.
 *
 * `source` is widened past CreateWorkflowPayload's to include "enrichment",
 * which the retry endpoints accept.
 */
export interface RetryWorkflowIdentity {
  personId: string;
  companyId: string;
  source?: "radar" | "workspace" | "enrichment";
  /** Required when source is "workspace" or "enrichment". */
  docId?: string;
  /** Required when source is "workspace". */
  columnName?: string;
}

export interface TryProviderPayload extends RetryWorkflowIdentity {
  providerKey: string;
}

export interface TryAllProvidersPayload extends RetryWorkflowIdentity {
  /** 1-20 unique keys. */
  providerKeys: string[];
}

export interface CreateWorkflowPayload {
  personId: string;
  companyId: string;
  /** Which Provider-Service surface the aggregator-backed providers call - "radar" (saved contact) or "workspace" (a table cell, requires docId/columnName). */
  source: "radar" | "workspace";
  docId?: string;
  columnName?: string;
}

export interface WorkflowSnapshotAttempt {
  rank: number;
  providerKey: string;
  providerName: string;
  creditCost: number;
  dispatchState: "dispatched" | "waiting_verification" | "timeout" | "resolved";
  /**
   * This provider's own verdict. Optional because older snapshots predate it;
   * the retry endpoints always populate it, and it is the only place a
   * per-provider "credit_exhausted" is reported.
   */
  status?: "found" | "not_found" | "credit_exhausted" | "in_progress";
  email: string | null;
  email_verification: string | null;
  dispatchedAt: string;
  resolvedAt: string | null;
}

export interface WorkflowProviderOrderEntry {
  providerKey: string;
  providerName: string;
  creditCost: number;
}

/**
 * POST /api/workflows and GET /api/workflows/:id's `data` shape - the full
 * current WorkflowView, used both to seed Redux right after creation and to
 * re-sync it after the Notification-Service stream reconnects (see
 * sidebar.tsx).
 */
export interface WorkflowSnapshot {
  _id: string;
  userId: string;
  category: "email";
  personId: string;
  companyId: string;
  /**
   * Which surface this workflow fills. Present on workspace runs (absent on
   * older radar snapshots) — the only thing linking a finished workflow back
   * to its grid cell.
   */
  source?: "radar" | "workspace";
  /** The workspace_data row id, set only when source is "workspace". */
  docId?: string | null;
  /** The enriched_data column, set only when source is "workspace". */
  columnName?: string | null;
  providerOrder: WorkflowProviderOrderEntry[];
  currentRank: number;
  attempts: WorkflowSnapshotAttempt[];
  email: string | null;
  email_verification: string | null;
  /** ISO timestamp once the waterfall stops - null while in progress. */
  completedAt: string | null;
  /**
   * Whole-workflow state. `in_progress` comes back from `/try` when an email
   * verification checkpoint hasn't settled - there is no GET to poll, so the
   * verdict arrives on the SSE stream keyed by this document's `_id`.
   */
  status?: "completed" | "in_progress";
  createdAt: string;
  updatedAt: string;
}

export interface BulkWorkflowItem {
  personId: string;
  companyId: string;
}

export interface CreateBulkWorkflowPayload {
  items: BulkWorkflowItem[];
  source: "radar" | "workspace";
}

/**
 * POST /api/workflows/bulk and GET /api/workflows/bulk/:id's `data` shape.
 *
 * This is an AGGREGATE doc, not one WorkflowSnapshot per item - the bulk
 * endpoint has no per-item breakdown at all (confirmed live against the dev
 * Workflow service). Per-contact email results only ever land in the
 * contact's own saved-contact record, same as a single workflow - so the
 * only thing this doc is good for is a progress bar, and the caller must
 * re-fetch contacts once `completedAt` is non-null to see the real values.
 */
export interface BulkWorkflowSnapshot {
  _id: string;
  userId: string;
  category: "email";
  source: "radar" | "workspace";
  total: number;
  duplicates: number;
  counts: {
    completed: number;
    found: number;
    notFound: number;
    creditExhausted: number;
  };
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export const workflowApi = createApi({
  reducerPath: "workflowApi",
  baseQuery: axiosBaseQuery("/api/workflows"),
  tagTypes: ["Workflow", "BulkBatches"],
  endpoints: (builder) => ({
    createWorkflow: builder.mutation<{ success: boolean; message?: string; data: WorkflowSnapshot }, CreateWorkflowPayload>({
      query: (payload) => ({
        url: "",
        method: "POST",
        body: payload,
      }),
    }),
    getWorkflow: builder.query<WorkflowSnapshot, string>({
      query: (id) => ({ url: `/${id}`, method: "GET" }),
      transformResponse: (response: { data: WorkflowSnapshot }) => response.data,
    }),
    createBulkWorkflow: builder.mutation<{ success: boolean; message?: string; data: BulkWorkflowSnapshot }, CreateBulkWorkflowPayload>({
      query: (payload) => ({
        url: "/bulk",
        method: "POST",
        body: payload,
      }),
    }),
    getBulkWorkflow: builder.query<BulkWorkflowSnapshot, string>({
      query: (id) => ({ url: `/bulk/${id}`, method: "GET" }),
      transformResponse: (response: { data: BulkWorkflowSnapshot }) => response.data,
    }),
    /**
     * Every email batch of this user that is still running, newest first.
     *
     * The list is IN-PROGRESS ONLY - a batch leaves it the moment it
     * completes, so an empty array means "nothing running" and a batch that
     * vanishes between two reads finished (it did not fail). userId comes
     * from the session server-side, so there's no argument to pass.
     *
     * This is what lets the notification panel show batches this browser
     * session never started - another tab, or a run from before a reload.
     */
    listBulkWorkflows: builder.query<BulkWorkflowSnapshot[], void>({
      query: () => ({ url: "/bulk", method: "GET" }),
      transformResponse: (response: { data: BulkWorkflowSnapshot[] }) => response.data ?? [],
      providesTags: ["BulkBatches"],
    }),

    /**
     * Re-run ONE named email provider, bypassing the saved waterfall order.
     *
     * May resolve with `status: "in_progress"` when a verification checkpoint
     * hasn't settled. There is no GET to poll, so in that case the caller
     * matches the returned `_id` against the live SSE stream's `workflowId`.
     */
    tryProvider: builder.mutation<{ success: boolean; message?: string; data: WorkflowSnapshot }, TryProviderPayload>({
      query: (payload) => ({ url: "/try", method: "POST", body: payload }),
    }),

    /**
     * Re-run every listed email provider concurrently. Blocks until each one
     * and its verification checkpoint has a verdict, so this can take tens of
     * seconds - the server allows 60s for it.
     */
    tryAllProviders: builder.mutation<{ success: boolean; message?: string; data: WorkflowSnapshot }, TryAllProvidersPayload>({
      query: (payload) => ({ url: "/try-all", method: "POST", body: payload }),
    }),
  }),
});

export const {
  useCreateWorkflowMutation,
  useLazyGetWorkflowQuery,
  useCreateBulkWorkflowMutation,
  useLazyGetBulkWorkflowQuery,
  useListBulkWorkflowsQuery,
  useLazyListBulkWorkflowsQuery,
  useTryProviderMutation,
  useTryAllProvidersMutation,
} = workflowApi;

export default workflowApi;
